import { signal } from "@preact/signals";
import { HP_PER_LEVEL } from "@/config";
import type { HeroAvatar } from "@/content/heroes";
import { getItem, slotOf, type EquipSlot, type ItemStats } from "@/content/items";
import {
  topicsForLevel,
  type AnswerPreference,
  type Lang,
  type LevelId,
  type LocalizedText,
  type Tier,
  type TopicStat,
} from "@/math";

export type Screen = "title" | "world";

export type QuestStatus = "active" | "done";

export interface QuestProgress {
  status: QuestStatus;
  progress: number;
}

export interface Equipment {
  weapon: string | null;
  armor: string | null;
  accessory: string | null;
}

/** One answered task, kept for the teacher report. */
export interface AttemptRecord {
  at: string;
  topicId: string;
  tier: Tier;
  prompt: string;
  answer: string;
  given: string;
  correct: boolean;
  timeMs: number;
  context: "battle" | "training";
}

export const HISTORY_LIMIT = 3000;

export interface HeroState {
  name: string;
  avatar: HeroAvatar;
  level: LevelId;
  lvl: number;
  xp: number;
  hp: number;
  hpMax: number;
  gold: number;
  inventory: Record<string, number>;
  equipment: Equipment;
  quests: Record<string, QuestProgress>;
  streak: number;
  bestStreak: number;
  answered: number;
  correct: number;
  stats: Record<string, TopicStat>;
  recentTopics: string[];
  defeated: Record<string, number>;
  flags: Record<string, boolean>;
  history: AttemptRecord[];
  /** Topic ids the hero is quizzed on; undefined or empty = every topic of the level. */
  topics?: string[];
  pos?: { x: number; y: number };
}

/** Topic ids used for this hero's battles and mixed training (the level's topics, narrowed by the choice). */
export function heroTopics(h: HeroState): string[] {
  const all = topicsForLevel(h.level).map((t) => t.id);
  const chosen = h.topics && h.topics.length > 0 ? all.filter((id) => h.topics!.includes(id)) : all;
  return chosen.length > 0 ? chosen : all;
}

export function defaultHero(name = "Bohater", level: LevelId = "sp6", avatar: HeroAvatar = "boy"): HeroState {
  return {
    name,
    avatar,
    level,
    lvl: 1,
    xp: 0,
    hp: 30,
    hpMax: 30,
    gold: 10,
    inventory: { potion: 2 },
    equipment: { weapon: "sword_wood", armor: "tunic_cloth", accessory: null },
    quests: {},
    streak: 0,
    bestStreak: 0,
    answered: 0,
    correct: 0,
    stats: {},
    recentTopics: [],
    defeated: {},
    flags: {},
    history: [],
  };
}

/** Append a task to the history, dropping the oldest entries past the limit. */
export function recordAttempt(h: HeroState, rec: AttemptRecord): HeroState {
  const history =
    h.history.length >= HISTORY_LIMIT
      ? [...h.history.slice(h.history.length - HISTORY_LIMIT + 1), rec]
      : [...h.history, rec];
  return { ...h, history };
}

export interface DialogChoice {
  label: LocalizedText;
  onSelect: () => void;
}

export interface DialogState {
  speaker: LocalizedText;
  lines: LocalizedText[];
  index: number;
  openedAt: number;
  /** Called once the last line has been dismissed (e.g. to open a shop or training). */
  onClose?: () => void;
  /** Portrait image URL shown next to the text. */
  faceset?: string;
  /** Buttons shown on the last line instead of "close"; the last one is the cancel choice (Esc). */
  choices?: DialogChoice[];
}

export interface Settings {
  timerSeconds: number;
  preference: AnswerPreference;
  /** Built-in calculator allowed in battles and training. */
  calculator: boolean;
}

export type PanelId = "backpack" | "shop" | "report-history" | "report-curriculum" | "help";

export const lang = signal<Lang>("pl");
export const screen = signal<Screen>("title");
export const hero = signal<HeroState>(defaultHero());
export const dialog = signal<DialogState | null>(null);
export const toast = signal<{ text: string; until: number } | null>(null);
export const settings = signal<Settings>({ timerSeconds: 0, preference: "auto", calculator: true });
/** Which HTML panel is open (besides battle/training/dialog). */
export const panel = signal<PanelId | null>(null);
export type BackpackTab = "equipment" | "quests" | "progress";
/** Tab the backpack opens on (the toolbar jumps straight to quests or progress). */
export const backpackTab = signal<BackpackTab>("equipment");

/** Open the backpack on a given tab, or close it when that tab is already showing. */
export function openBackpack(tab: BackpackTab = "equipment"): void {
  if (panel.value === "backpack" && backpackTab.value === tab) {
    panel.value = null;
    return;
  }
  backpackTab.value = tab;
  panel.value = "backpack";
}
/** The built-in calculator window. */
export const calculatorOpen = signal(false);

/** Touch d-pad state, read by the World scene every frame. Plain object on purpose (no re-renders). */
export const touch = { up: false, down: false, left: false, right: false, action: false };

export function xpToNext(lvl: number): number {
  return 30 + (lvl - 1) * 25;
}

export function applyXp(h: HeroState, amount: number): { hero: HeroState; leveledUp: boolean } {
  let xp = h.xp + amount;
  let lvl = h.lvl;
  let hpMax = h.hpMax;
  let leveledUp = false;
  while (xp >= xpToNext(lvl)) {
    xp -= xpToNext(lvl);
    lvl += 1;
    hpMax += HP_PER_LEVEL;
    leveledUp = true;
  }
  return { hero: { ...h, xp, lvl, hpMax, hp: leveledUp ? hpMax : h.hp }, leveledUp };
}

/** Combined stats from everything the hero wears. */
export function heroStats(h: HeroState): Required<ItemStats> {
  const total: Required<ItemStats> = { atk: 0, def: 0, timerBonusSec: 0, freeHints: 0 };
  for (const id of [h.equipment.weapon, h.equipment.armor, h.equipment.accessory]) {
    if (!id) continue;
    const s = getItem(id).stats ?? {};
    total.atk += s.atk ?? 0;
    total.def += s.def ?? 0;
    total.timerBonusSec += s.timerBonusSec ?? 0;
    total.freeHints += s.freeHints ?? 0;
  }
  return total;
}

export function itemCount(h: HeroState, itemId: string): number {
  return h.inventory[itemId] ?? 0;
}

export function addItems(h: HeroState, items: Record<string, number>): HeroState {
  const inventory = { ...h.inventory };
  for (const [id, n] of Object.entries(items)) inventory[id] = (inventory[id] ?? 0) + n;
  return { ...h, inventory };
}

/** Remove one unit; returns null when the hero does not have it. */
export function removeItem(h: HeroState, itemId: string, n = 1): HeroState | null {
  const have = h.inventory[itemId] ?? 0;
  if (have < n) return null;
  const inventory = { ...h.inventory };
  if (have === n) delete inventory[itemId];
  else inventory[itemId] = have - n;
  return { ...h, inventory };
}

/** Equip an item from the inventory; the previously equipped item goes back to the inventory. */
export function equipItem(h: HeroState, itemId: string): HeroState {
  const item = getItem(itemId);
  const slot: EquipSlot | null = slotOf(item);
  if (!slot) return h;
  const without = removeItem(h, itemId);
  if (!without) return h;
  const previous = h.equipment[slot];
  const next = previous ? addItems(without, { [previous]: 1 }) : without;
  return { ...next, equipment: { ...next.equipment, [slot]: itemId } };
}

export function unequipSlot(h: HeroState, slot: EquipSlot): HeroState {
  const current = h.equipment[slot];
  if (!current) return h;
  const withItem = addItems(h, { [current]: 1 });
  return { ...withItem, equipment: { ...withItem.equipment, [slot]: null } };
}

export function showToast(text: string, ms = 2500): void {
  toast.value = { text, until: performance.now() + ms };
  setTimeout(() => {
    if (toast.value && performance.now() >= toast.value.until) toast.value = null;
  }, ms + 20);
}

export function openDialog(
  speaker: LocalizedText,
  lines: LocalizedText[],
  onClose?: () => void,
  faceset?: string,
): void {
  const d: DialogState = { speaker, lines, index: 0, openedAt: performance.now() };
  if (onClose) d.onClose = onClose;
  if (faceset) d.faceset = faceset;
  dialog.value = d;
}

/** A question with buttons (e.g. "fight the guard?"). The last choice is what Esc picks. */
export function openChoice(
  speaker: LocalizedText,
  text: LocalizedText,
  choices: DialogChoice[],
  faceset?: string,
): void {
  const d: DialogState = { speaker, lines: [text], index: 0, openedAt: performance.now(), choices };
  if (faceset) d.faceset = faceset;
  dialog.value = d;
}

export function pickChoice(index: number): void {
  const d = dialog.value;
  if (!d || !d.choices) return;
  const choice = d.choices[index];
  if (!choice) return;
  dialog.value = null;
  choice.onSelect();
}

export function advanceDialog(): void {
  const d = dialog.value;
  if (!d) return;
  if (d.index + 1 >= d.lines.length) {
    if (d.choices) return; // the player must pick a button
    dialog.value = null;
    d.onClose?.();
  } else {
    dialog.value = { ...d, index: d.index + 1 };
  }
}

export function closeDialog(): void {
  const d = dialog.value;
  if (!d) return;
  if (d.choices) {
    pickChoice(d.choices.length - 1);
    return;
  }
  dialog.value = null;
  d.onClose?.();
}
